<template>
  <div class="w-full" dir="rtl">
    <div class="flex flex-col">
      <label for="datepicker-timeonly" class="font-medium text-sm">מזהה עסק ב-Google Reviews
        <info-button>
          לא יודעים איך למצוא את המזהה שלכם? <br/> לחצו על החיפוש, חפשו את העסק שלכם, ואנחנו כבר נעדכן מה שצריך.
        </info-button>
      </label>
      <span class="mb-2 text-xs text-gray-400 text-pretty">העסק שלכם מופיע בגוגל? רוצים לשלוח לקוחות גם לדרג את העסק שלכם? לאחר מילוי משוב חיובי, נשלח את הלקוח למלא גם פידבק בגוגל.</span>

      <div class="relative">
        <IconField>
          <InputText
            :model-value="modelValue"
            placeholder="לחצו לחיפוש העסק שלכם"
            class="w-full pr-10"
            readonly
            @click="openModal"
          />
          <InputIcon class="pi pi-search"/>
        </IconField>
      </div>
    </div>

    <Dialog
      v-model:visible="showModal"
      :header="'חיפוש עסק'"
      :style="{ width: '90vw', maxWidth: '800px' }"
      :modal="true"
      :closable="true"
      class="rtl-dialog"
    >
      <div class="flex flex-col gap-6 p-4" dir="rtl">
        <!-- Search Section -->
        <div class="flex flex-col gap-4">
          <h4 class="text-lg font-semibold text-gray-800">מצא את העסק שלך</h4>
          <div class="relative">
            <InputText
              ref="searchInput"
              v-model="searchQuery"
              :placeholder="'חפש עסק (לדוגמה: פיצה פלאס תל אביב)'"
              class="w-full"
              @input="onSearchInput"
            />
            <div v-if="isLoading" class="absolute left-3 top-1/2 transform -translate-y-1/2">
              <ProgressSpinner style="width: 20px; height: 20px" stroke-width="4" />
            </div>
          </div>

          <!-- Search Results -->
          <div v-if="searchResults.length > 0" class="flex flex-col gap-2">
            <h5 class="font-medium text-gray-700">תוצאות חיפוש:</h5>
            <div class="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
              <div
                v-for="place in searchResults"
                :key="place.id"
                class="flex items-center justify-between p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                @click="confirmSelection(place)"
              >
                <div class="flex-1">
                  <div class="font-semibold text-gray-800">{{ place.displayName }}</div>
                  <div class="text-sm text-gray-600 mt-1">{{ place.formattedAddress }}</div>
                </div>
                <Button
                  label="בחירה"
                  size="small"
                  @click.stop="confirmSelection(place)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <Message v-if="error" severity="error" :closable="false">
          {{ error }}
        </Message>
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref, watch} from 'vue'
import ProgressSpinner from 'primevue/progressspinner'
import InputIcon from "primevue/inputicon";
import IconField from "primevue/iconfield";
import InputText from "primevue/inputtext";
import { trackEvent } from '@app/composables/posthog';

interface BusinessPlace {
  id: string
  displayName?: { text: string }
  name?: string
  formattedAddress: string
  types: string[]
  location?: {
    lat: number
    lng: number
  }
}

interface Props {
  modelValue?: string
}

interface Emits {
  (e: 'update:modelValue', value: string): void
  (e: 'business-selected', data: {
    business: BusinessPlace
    placeId: string
    directReviewLink: string
    reviewsPageLink: string
  }): void
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
})

const emit = defineEmits<Emits>()

const showModal = ref(false)
const searchQuery = ref('')
const searchResults = ref<BusinessPlace[]>([])
const selectedBusiness = ref<BusinessPlace>()
const tempSelectedBusiness = ref<BusinessPlace>()
const isLoading = ref(false)
const isInitiated = ref(false)
const error = ref('')
const copied = ref(false)
const searchInput = ref<InstanceType<typeof InputText>>()

let searchTimeout: NodeJS.Timeout;
let placesLib: any = null


watch(() => props.modelValue, async (newPlaceId) => {
  if (newPlaceId && newPlaceId !== selectedBusiness.value?.id && isInitiated.value) {
    await loadBusinessByPlaceId(newPlaceId)
  }
}, { immediate: true })

// Methods
const loadGoogleMapsScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve()
      return
    }

    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_PLACES_API}&libraries=places&language=he&region=IL`
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Google Maps script'))
    document.head.appendChild(script)
  })
}

const initializePlacesAPI = async () => {
  try {
    await loadGoogleMapsScript()
    if (window.google && window.google.maps && window.google.maps.places) {
      const { Place } = await window.google.maps.importLibrary('places') as any
      placesLib = { Place }
      isInitiated.value = true;
    } else {
      throw new Error('Google Places library not available')
    }
  } catch (err) {
    error.value = 'שגיאה בטעינת Google Maps. אנא בדוק את מפתח ה-API ואת החיבור לאינטרנט.'
    console.error('Google Maps loading error:', err)
  }
}

const loadBusinessByPlaceId = async (placeId: string) => {
  if (!placesLib || !placeId || !isInitiated.value) return

  try {
    const place = new placesLib.Place({
      id: placeId,
      requestedLanguage: 'he'
    })

    await place.fetchFields({
      fields: ['id', 'displayName', 'formattedAddress', 'types', 'location']
    })

    selectedBusiness.value = {
      id: place.id,
      displayName: place.displayName,
      formattedAddress: place.formattedAddress,
      types: place.types || [],
      location: place.location
    }
  } catch (err) {
    console.error('Error loading business by Place ID:', err)
    error.value = 'שגיאה בטעינת פרטי העסק'
  }
}

const onSearchInput = () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }

  searchTimeout = setTimeout(() => {
    if (searchQuery.value.trim().length > 2) {
      searchBusinesses()
    } else {
      searchResults.value = []
    }
  }, 500)
}

const searchBusinesses = async () => {
  if (!placesLib) {
    error.value = 'שירות Google Places לא הותחל'
    return
  }

  isLoading.value = true
  error.value = ''

  try {
    const request = {
      textQuery: searchQuery.value,
      fields: ['id', 'displayName', 'formattedAddress', 'types'],
    }

    const { places } = await window.google.maps.places.Place.searchByText(request)

    if (places && places.length > 0) {
      searchResults.value = places.slice(0, 10).map((place: any) => ({
        id: place.id,
        displayName: place.displayName,
        formattedAddress: place.formattedAddress,
        types: place.types || []
      }))
    } else {
      searchResults.value = []
      error.value = 'לא נמצאו עסקים. נסה מונח חיפוש אחר.'
    }
  } catch (err) {
    error.value = 'החיפוש נכשל. אנא נסה שוב.'
    console.error('Places search failed:', err)
  } finally {
    isLoading.value = false
  }
}

const openModal = () => {
  trackEvent('google_business_search_opened');
  showModal.value = true
  tempSelectedBusiness.value = selectedBusiness.value
  setTimeout(() => {
    searchInput.value?.$el?.focus()
  }, 100)
}

const confirmSelection = (place: BusinessPlace) => {
  if (!place) return

  trackEvent('google_business_selected', { place_id: place.id });
  emit('update:modelValue', place.id)
  emit('business-selected', {
    business: place,
    placeId: place.id,
    directReviewLink: `https://search.google.com/local/writereview?placeid=${place.id}`,
    reviewsPageLink: `https://search.google.com/local/reviews?placeid=${place.id}`
  })

  searchQuery.value = ''
  searchResults.value = []
  error.value = ''
  showModal.value = false
}

// Lifecycle
onMounted(async () => {
  await initializePlacesAPI()
})
</script>

<style scoped>
.rtl-dialog :deep(.p-dialog) {
  direction: rtl;
}

.rtl-dialog :deep(.p-dialog-header) {
  text-align: right;
}

.rtl-dialog :deep(.p-dialog-content) {
  direction: rtl;
}

.rtl-dialog :deep(.p-dialog-footer) {
  direction: rtl;
  justify-content: flex-start;
}

.rtl-dialog :deep(.p-dialog-footer .flex) {
  direction: ltr;
}
</style>
